import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import Clipboard from "@react-native-clipboard/clipboard";

import { PrimaryButton, SecondaryButton, TitleText } from "../../common/native";
import Input from "../../common/native/Input";
import { SessionContext } from "../../../contexts";
import {
  getBillingAdminClinicSignups,
  BillingAdminApiError,
  TAdminClinicSignupUser,
  TBillingAdminAuth,
  TBillingAdminClinicHistoryResponse,
  TBillingAdminClinicSummary,
  TBillingAdminInvoice,
  TBillingAdminSummaryResponse,
  getBillingAdminClinicHistory,
  getBillingAdminSummary,
} from "../../../services/api-billing-admin";
import { mixpanel } from "../../../utils/mixpanel";

type SortColumn =
  | "clinic"
  | "clinic_created"
  | "plan"
  | "delinquent"
  | "remaining_sims"
  | "latest_invoice";

type SummaryError = {
  message: string;
  status?: number;
};

const formatCurrency = (amountInCents?: number | null, currency = "usd") => {
  if (amountInCents === null || amountInCents === undefined) return "—";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amountInCents / 100);
  } catch {
    return `$${(amountInCents / 100).toFixed(2)}`;
  }
};

const formatTimestamp = (iso?: string | null) => {
  if (!iso) return { local: "—", utc: "—" };
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { local: String(iso), utc: "—" };
  return {
    local: date.toLocaleString(),
    utc: date.toUTCString(),
  };
};

const formatEpochSeconds = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined)
    return { local: "—", utc: "—" };
  const date = new Date(seconds * 1000);
  if (Number.isNaN(date.getTime())) return { local: String(seconds), utc: "—" };
  return {
    local: date.toLocaleString(),
    utc: date.toUTCString(),
  };
};

const formatEpochLike = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") {
    return { local: "—", utc: "—" };
  }
  if (typeof value === "number") {
    return formatEpochSeconds(value);
  }
  const asNumber = Number(value);
  if (Number.isFinite(asNumber) && String(asNumber) === String(value).trim()) {
    return formatEpochSeconds(asNumber);
  }
  return formatTimestamp(value);
};

const getClinicPlan = (clinic: TBillingAdminClinicSummary) =>
  clinic.plan_type ?? clinic.current_plan ?? "";

const getClinicCreatedAt = (clinic: TBillingAdminClinicSummary) =>
  clinic.clinic_created_at ?? clinic.created_at ?? null;

const DEFAULT_EXCLUDED_CLINIC_TERMS = ["test", "phu", "abate", "kav"];

const BillingAdminPage: React.FC = () => {
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;

  const [activeAuthMode, setActiveAuthMode] = useState<"apiKey" | "jwt">(
    "apiKey",
  );
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [authSecret, setAuthSecret] = useState<TBillingAdminAuth | null>(null);
  const [summary, setSummary] = useState<TBillingAdminSummaryResponse | null>(
    null,
  );
  const [summaryError, setSummaryError] = useState<SummaryError | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  const [selectedClinicId, setSelectedClinicId] = useState<string | null>(null);
  const [clinicHistory, setClinicHistory] =
    useState<TBillingAdminClinicHistoryResponse | null>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [clinicSignupUsers, setClinicSignupUsers] = useState<
    TAdminClinicSignupUser[]
  >([]);
  const [isClinicUsersLoading, setIsClinicUsersLoading] = useState(false);
  const [clinicUsersError, setClinicUsersError] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    plan: "ALL",
    delinquentOnly: false,
    search: "",
  });
  const [excludedClinicTerms, setExcludedClinicTerms] = useState<string[]>(
    DEFAULT_EXCLUDED_CLINIC_TERMS,
  );
  const [newExcludedTerm, setNewExcludedTerm] = useState("");
  const [sortState, setSortState] = useState<{
    column: SortColumn;
    direction: "asc" | "desc";
  } | null>(null);

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [needsCredentialReset, setNeedsCredentialReset] = useState(false);

  useEffect(() => {
    if (!copiedField) return;

    const timer = setTimeout(() => setCopiedField(null), 2000);
    return () => clearTimeout(timer);
  }, [copiedField]);

  const handleAuthFailure = useCallback((error: BillingAdminApiError) => {
    setSummary(null);
    setClinicHistory(null);
    setSelectedClinicId(null);
    setAuthSecret(null);
    setNeedsCredentialReset(true);
    setSummaryError({ message: error.message, status: error.status });
  }, []);

  const loadSummary = useCallback(
    async (currentAuth: TBillingAdminAuth) => {
      setIsSummaryLoading(true);
      setSummaryError(null);
      setNeedsCredentialReset(false);
      try {
        const response = await getBillingAdminSummary(currentAuth, {
          excludeClinicNameContains: excludedClinicTerms,
        });
        setSummary(response);
      } catch (error) {
        if (error instanceof BillingAdminApiError) {
          if (error.status === 401 || error.status === 403) {
            handleAuthFailure(error);
            return;
          }
          setSummaryError({ message: error.message, status: error.status });
        } else if (error instanceof Error) {
          setSummaryError({ message: error.message });
        } else {
          setSummaryError({ message: "Failed to load billing summary." });
        }
      } finally {
        setIsSummaryLoading(false);
      }
    },
    [handleAuthFailure, excludedClinicTerms],
  );

  useEffect(() => {
    if (!authSecret) return;
    loadSummary(authSecret);
  }, [authSecret, loadSummary]);

  const handleConnectWithApiKey = () => {
    const trimmedKey = apiKeyInput.trim();
    if (!trimmedKey) {
      setSummaryError({
        message: "Enter your billing admin key to connect.",
      });
      return;
    }
    setSummary(null);
    setClinicHistory(null);
    setSelectedClinicId(null);
    setSummaryError(null);
    setHistoryError(null);
    setNeedsCredentialReset(false);
    setAuthSecret({ type: "apiKey", secret: trimmedKey });
  };

  const handleConnectWithJwt = () => {
    const token = session?.accessToken;
    if (!token) {
      setSummaryError({
        message: "Sign in to the admin portal to use the JWT flow.",
      });
      return;
    }
    setSummary(null);
    setClinicHistory(null);
    setSelectedClinicId(null);
    setSummaryError(null);
    setHistoryError(null);
    setNeedsCredentialReset(false);
    setAuthSecret({ type: "jwt", secret: token });
  };

  const handleResetCredentials = () => {
    setApiKeyInput("");
    setSummary(null);
    setClinicHistory(null);
    setSelectedClinicId(null);
    setSummaryError(null);
    setHistoryError(null);
    setAuthSecret(null);
    setNeedsCredentialReset(false);
    setFilters({
      plan: "ALL",
      delinquentOnly: false,
      search: "",
    });
    setSortState(null);
    setExcludedClinicTerms(DEFAULT_EXCLUDED_CLINIC_TERMS);
    setNewExcludedTerm("");
  };
  const handleRemoveExcludedTerm = (term: string) => {
    setExcludedClinicTerms((current) =>
      current.filter((value) => value.toLowerCase() !== term.toLowerCase()),
    );
  };

  const handleAddExcludedTerm = () => {
    const trimmed = newExcludedTerm.trim();
    if (!trimmed) return;
    const exists = excludedClinicTerms.some(
      (term) => term.toLowerCase() === trimmed.toLowerCase(),
    );
    if (!exists) {
      setExcludedClinicTerms((current) => [...current, trimmed]);
    }
    setNewExcludedTerm("");
  };

  const handleCopy = (value: string, label: string) => {
    Clipboard.setString(value);
    setCopiedField(label);
  };

  const handleSort = (column: SortColumn) => {
    setSortState((prev) => {
      if (!prev || prev.column !== column) {
        return {
          column,
          direction: column === "clinic_created" ? "desc" : "asc",
        };
      }
      return {
        column,
        direction: prev.direction === "asc" ? "desc" : "asc",
      };
    });
  };

  const filteredClinics = useMemo(() => {
    if (!summary?.clinics?.length) return [];

    let clinics = [...summary.clinics];
    const searchTerm = filters.search.trim().toLowerCase();

    if (filters.delinquentOnly) {
      clinics = clinics.filter((clinic) => clinic.is_delinquent);
    }

    if (filters.plan !== "ALL") {
      clinics = clinics.filter(
        (clinic) => getClinicPlan(clinic) === filters.plan,
      );
    }

    if (searchTerm) {
      clinics = clinics.filter(
        (clinic) =>
          clinic.clinic_name.toLowerCase().includes(searchTerm) ||
          clinic.clinic_uuid.toLowerCase().includes(searchTerm),
      );
    }

    if (sortState) {
      clinics.sort((a, b) => {
        const directionMultiplier = sortState.direction === "asc" ? 1 : -1;
        switch (sortState.column) {
          case "clinic":
            return (
              a.clinic_name.localeCompare(b.clinic_name) * directionMultiplier
            );
          case "clinic_created": {
            const tsA = getClinicCreatedAt(a)
              ? new Date(getClinicCreatedAt(a)!).getTime()
              : NaN;
            const tsB = getClinicCreatedAt(b)
              ? new Date(getClinicCreatedAt(b)!).getTime()
              : NaN;
            const aValid = Number.isFinite(tsA);
            const bValid = Number.isFinite(tsB);
            if (!aValid && !bValid) return 0;
            if (!aValid) return 1;
            if (!bValid) return -1;
            return (tsA - tsB) * directionMultiplier;
          }
          case "plan": {
            const planA = getClinicPlan(a);
            const planB = getClinicPlan(b);
            return planA.localeCompare(planB) * directionMultiplier;
          }
          case "delinquent":
            return (
              (Number(a.is_delinquent) - Number(b.is_delinquent)) *
              directionMultiplier
            );
          case "remaining_sims":
            return (
              ((a.remaining_free_simulation_count || 0) -
                (b.remaining_free_simulation_count || 0)) *
              directionMultiplier
            );
          case "latest_invoice": {
            const tsA = a.latest_invoice?.created_at
              ? new Date(a.latest_invoice.created_at).getTime()
              : NaN;
            const tsB = b.latest_invoice?.created_at
              ? new Date(b.latest_invoice.created_at).getTime()
              : NaN;
            const aValid = Number.isFinite(tsA);
            const bValid = Number.isFinite(tsB);
            if (!aValid && !bValid) return 0;
            if (!aValid) return 1;
            if (!bValid) return -1;
            return (tsA - tsB) * directionMultiplier;
          }
          default:
            return 0;
        }
      });
    }

    return clinics;
  }, [summary, filters, sortState]);

  useEffect(() => {
    if (
      selectedClinicId &&
      !summary?.clinics.find(
        (clinic) => clinic.clinic_uuid === selectedClinicId,
      )
    ) {
      setSelectedClinicId(null);
      setClinicHistory(null);
    }
  }, [summary, selectedClinicId]);

  const uniquePlanTypes = useMemo(() => {
    const fromClinics =
      summary?.clinics?.map((clinic) => getClinicPlan(clinic)) || [];
    const fromPlanMix = Object.keys(summary?.by_plan_type || {});
    const planValues = [...fromClinics, ...fromPlanMix];
    return Array.from(new Set(planValues))
      .map((value) => ({
        value,
        label: value?.trim().length ? value : "Unassigned plan",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [summary]);

  const planMixEntries = useMemo(() => {
    if (!summary) return [];
    const byPlanType = summary.by_plan_type || {};
    const clinicPlanKeys = summary.clinics.map((clinic) =>
      getClinicPlan(clinic),
    );
    const allPlanKeys = Array.from(
      new Set([...Object.keys(byPlanType), ...clinicPlanKeys]),
    );
    return allPlanKeys
      .map((key) => ({
        key,
        label: key?.trim().length ? key : "Unassigned plan",
        count: byPlanType[key] ?? 0,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [summary]);

  const delinquentClinics = useMemo(
    () => summary?.clinics.filter((clinic) => clinic.is_delinquent) || [],
    [summary],
  );

  const clinicHistorySubscriptions = clinicHistory?.subscriptions ?? [];
  const clinicHistoryInvoices = clinicHistory?.invoices ?? [];

  const handleFetchClinicHistory = async (
    clinic: TBillingAdminClinicSummary,
  ) => {
    if (!authSecret) {
      setHistoryError("Connect with credentials to view history.");
      return;
    }

    setSelectedClinicId(clinic.clinic_uuid);
    setClinicHistory(null);
    setHistoryError(null);
    setClinicSignupUsers([]);
    setClinicUsersError(null);
    setIsHistoryLoading(true);
    setIsClinicUsersLoading(true);

    try {
      const [history] = await Promise.all([
        getBillingAdminClinicHistory(clinic.clinic_uuid, authSecret),
        (async () => {
          let cursor: string | undefined;
          let foundUsers: TAdminClinicSignupUser[] = [];
          let pagesChecked = 0;
          const maxPagesToCheck = 20;

          while (pagesChecked < maxPagesToCheck) {
            const signupPage = await getBillingAdminClinicSignups(authSecret, {
              limit: 200,
              includeDemo: true,
              cursor,
            });
            const matchedClinic = signupPage.clinics.find(
              (item) => item.clinic_uuid === clinic.clinic_uuid,
            );
            if (matchedClinic) {
              foundUsers = matchedClinic.users || [];
              break;
            }

            if (!signupPage.next_cursor) {
              break;
            }
            cursor = signupPage.next_cursor || undefined;
            pagesChecked += 1;
          }

          setClinicSignupUsers(foundUsers);
        })(),
      ]);
      setClinicHistory(history);
      mixpanel.track("Billing Admin - Clinic History Viewed", {
        clinic_uuid: clinic.clinic_uuid,
        clinic_name: clinic.clinic_name,
        viewed_at: new Date().toISOString(),
        auth_mode: authSecret.type,
      });
    } catch (error) {
      if (error instanceof BillingAdminApiError) {
        if (error.status === 401 || error.status === 403) {
          handleAuthFailure(error);
          setHistoryError(null);
          setClinicUsersError(null);
          return;
        }
        setHistoryError(error.message);
        setClinicUsersError(error.message);
      } else if (error instanceof Error) {
        setHistoryError(error.message);
        setClinicUsersError(error.message);
      } else {
        setHistoryError("Failed to load clinic history.");
        setClinicUsersError("Failed to load clinic users.");
      }
    } finally {
      setIsHistoryLoading(false);
      setIsClinicUsersLoading(false);
    }
  };

  const handleExportDelinquentClinics = () => {
    if (
      !delinquentClinics.length ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    )
      return;

    const headers = [
      "clinic_uuid",
      "clinic_name",
      "plan_type",
      "remaining_free_simulation_count",
      "latest_invoice_id",
      "latest_invoice_status",
    ];

    const rows = delinquentClinics.map((clinic) => [
      clinic.clinic_uuid,
      clinic.clinic_name,
      getClinicPlan(clinic),
      `${clinic.remaining_free_simulation_count ?? ""}`,
      clinic.latest_invoice?.id ?? "",
      clinic.latest_invoice?.status ?? "",
    ]);

    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `delinquent-clinics-${new Date().toISOString()}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderInvoiceLink = (invoice?: TBillingAdminInvoice | null) => {
    if (!invoice) return <Text style={styles.tableText}>—</Text>;

    const timestamp = formatTimestamp(invoice.created_at);
    const nextAttempt = formatEpochSeconds(invoice.next_payment_attempt);
    const hasInvoiceUrl = Boolean(invoice.hosted_invoice_url);

    return (
      <View style={styles.invoiceCell}>
        <Text style={styles.invoiceId}>{invoice.id}</Text>
        <Text style={styles.invoiceStatus}>{invoice.status || "unknown"}</Text>
        <Text style={styles.invoiceAmount}>
          {formatCurrency(invoice.amount_paid, invoice.currency || "usd")}
        </Text>
        <Text
          style={styles.invoiceTimestamp}
          {...(Platform.OS === "web"
            ? {
                // @ts-ignore react-native-web prop
                title: nextAttempt.utc,
              }
            : {})}
        >
          Next attempt: {nextAttempt.local}
        </Text>
        <Text
          style={[
            styles.invoiceLink,
            !hasInvoiceUrl && styles.invoiceLinkDisabled,
          ]}
          onPress={() => {
            if (hasInvoiceUrl && typeof window !== "undefined") {
              window.open(invoice.hosted_invoice_url!, "_blank");
            }
          }}
        >
          {hasInvoiceUrl ? "Open invoice" : "Link unavailable"}
        </Text>
        {invoice.hosted_invoice_url && (
          <SecondaryButton
            text={
              copiedField === invoice.hosted_invoice_url
                ? "Copied"
                : "Copy link"
            }
            onPress={() =>
              handleCopy(
                invoice.hosted_invoice_url!,
                invoice.hosted_invoice_url!,
              )
            }
            style={styles.copyButton}
          />
        )}
        <Text
          style={styles.invoiceTimestamp}
          {...(Platform.OS === "web"
            ? {
                // @ts-ignore react-native-web prop
                title: timestamp.utc,
              }
            : {})}
        >
          {timestamp.local}
        </Text>
      </View>
    );
  };

  const activeAuthBadge = authSecret
    ? authSecret.type === "apiKey"
      ? "Billing admin key"
      : "Admin JWT"
    : "Disconnected";

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
      <View style={styles.headerRow}>
        <View style={styles.headerTextGroup}>
          <TitleText text="Billing Admin Dashboard" />
          <Text style={styles.subtitle}>
            Internal sales intelligence view for aggregate billing health and
            clinic-level history.
          </Text>
        </View>
        <View style={styles.badgeWrapper}>
          <Text style={styles.authBadgeLabel}>Auth mode</Text>
          <Text style={[styles.authBadge, !authSecret && styles.authBadgeIdle]}>
            {activeAuthBadge}
          </Text>
        </View>
      </View>

      <View style={styles.connectionCard}>
        <Text style={styles.cardTitle}>Connection drawer</Text>
        <Text style={styles.cardSubtitle}>
          Choose an auth path each session. Secrets stay in memory only and
          clear on refresh or reset.
        </Text>
        <View style={styles.authToggleRow}>
          <SecondaryButton
            text="Billing Admin Key"
            onPress={() => setActiveAuthMode("apiKey")}
            selected={activeAuthMode === "apiKey"}
            style={styles.authToggleButton}
          />
          <SecondaryButton
            text="Admin JWT"
            onPress={() => setActiveAuthMode("jwt")}
            selected={activeAuthMode === "jwt"}
            style={styles.authToggleButton}
          />
        </View>

        {activeAuthMode === "apiKey" && (
          <View style={styles.authContent}>
            <Input
              name="Billing Admin Key"
              placeholder="Paste admin key"
              secureTextEntry
              value={apiKeyInput}
              onChangeText={setApiKeyInput}
              autoCapitalize="none"
            />
            <PrimaryButton
              text="Connect with key"
              onPress={handleConnectWithApiKey}
              style={styles.connectButton}
            />
          </View>
        )}

        {activeAuthMode === "jwt" && (
          <View style={styles.authContent}>
            <View style={styles.jwtInfo}>
              {session ? (
                <>
                  <Text style={styles.jwtConnected}>
                    Signed in as {session.userEmail}
                  </Text>
                  <Text style={styles.jwtHint}>
                    Your Supabase session token will be used as the admin JWT.
                  </Text>
                </>
              ) : (
                <Text style={styles.jwtHint}>
                  Sign in through the admin portal to enable JWT access.
                </Text>
              )}
            </View>
            <PrimaryButton
              text="Use current admin session"
              onPress={handleConnectWithJwt}
              disabled={!session?.accessToken}
              style={styles.connectButton}
            />
          </View>
        )}

        <SecondaryButton
          text="Reset credentials"
          onPress={handleResetCredentials}
          style={styles.resetButton}
        />
      </View>

      {needsCredentialReset && (
        <View style={styles.warningCard}>
          <Text style={styles.warningTitle}>Credentials rejected</Text>
          <Text style={styles.warningMessage}>
            {summaryError?.status === 401
              ? "Unauthorized (401). Re-enter your billing admin key or start a new admin session."
              : summaryError?.status === 403
                ? "Forbidden (403). Your current key/session is missing required permissions."
                : `The server returned ${summaryError?.status}. Re-enter your key or re-authenticate before continuing.`}
          </Text>
        </View>
      )}

      <View style={styles.summarySection}>
        {isSummaryLoading && (
          <View style={styles.loaderRow}>
            <ActivityIndicator color="#00B7F4" />
            <Text style={styles.loaderText}>Loading billing summary…</Text>
          </View>
        )}
        {summaryError && !isSummaryLoading && (
          <View style={styles.errorCard}>
            <Text style={styles.errorTitle}>
              Unable to load billing summary
            </Text>
            <Text style={styles.errorMessage}>{summaryError.message}</Text>
            {summaryError.status &&
              summaryError.status >= 500 &&
              authSecret && (
                <PrimaryButton
                  text="Retry"
                  onPress={() => loadSummary(authSecret)}
                  style={styles.retryButton}
                />
              )}
          </View>
        )}

        {summary && !isSummaryLoading && (
          <>
            <View style={styles.kpiRow}>
              <View style={styles.kpiCard}>
                <Text style={styles.kpiLabel}>Total clinics</Text>
                <Text style={styles.kpiValue}>
                  {summary?.total_clinics ?? "—"}
                </Text>
              </View>
              <View style={styles.kpiCard}>
                <Text style={styles.kpiLabel}>Free</Text>
                <Text style={styles.kpiValue}>{summary?.free ?? "—"}</Text>
              </View>
              <View style={styles.kpiCard}>
                <Text style={styles.kpiLabel}>Paid</Text>
                <Text style={styles.kpiValue}>{summary?.paid ?? "—"}</Text>
              </View>
              <View style={styles.kpiCard}>
                <Text style={styles.kpiLabel}>Delinquent</Text>
                <Text style={styles.kpiValueDanger}>
                  {summary?.delinquent ?? "—"}
                </Text>
              </View>
            </View>
            <View style={styles.planMixSection}>
              <Text style={styles.filterLabel}>Plan mix</Text>
              {!planMixEntries.length ? (
                <Text style={styles.toggleHint}>
                  No plan mix data returned.
                </Text>
              ) : (
                <View style={styles.planMixList}>
                  {planMixEntries.map((entry) => {
                    const maxCount = Math.max(
                      1,
                      ...planMixEntries.map((item) => item.count),
                    );
                    return (
                      <View
                        key={entry.key || "unassigned-plan"}
                        style={styles.planMixRow}
                      >
                        <Text style={styles.planMixLabel}>{entry.label}</Text>
                        <View style={styles.planMixBarTrack}>
                          <View
                            style={[
                              styles.planMixBarFill,
                              { width: `${(entry.count / maxCount) * 100}%` },
                            ]}
                          />
                        </View>
                        <Text style={styles.planMixCount}>{entry.count}</Text>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
            <View style={styles.filterRow}>
              <View style={styles.filterGroup}>
                <Input
                  name="Search clinics"
                  placeholder="Clinic name or UUID"
                  value={filters.search}
                  onChangeText={(text) =>
                    setFilters((current) => ({ ...current, search: text }))
                  }
                />
              </View>
              <View style={styles.filterGroup}>
                <Text style={styles.filterLabel}>Plan filter</Text>
                <View style={styles.planFilterRow}>
                  <SecondaryButton
                    text="All plans"
                    selected={filters.plan === "ALL"}
                    onPress={() =>
                      setFilters((current) => ({ ...current, plan: "ALL" }))
                    }
                    style={styles.planFilterButton}
                  />
                  {uniquePlanTypes.map(({ value, label }) => (
                    <SecondaryButton
                      key={value || "unassigned-plan"}
                      text={label}
                      selected={filters.plan === value}
                      onPress={() =>
                        setFilters((current) => ({
                          ...current,
                          plan: value,
                        }))
                      }
                      style={styles.planFilterButton}
                    />
                  ))}
                </View>
              </View>
              <View style={styles.filterGroup}>
                <Text style={styles.filterLabel}>Delinquent only</Text>
                <Pressable
                  style={[
                    styles.togglePill,
                    filters.delinquentOnly && styles.togglePillActive,
                  ]}
                  onPress={() =>
                    setFilters((current) => ({
                      ...current,
                      delinquentOnly: !current.delinquentOnly,
                    }))
                  }
                >
                  <View
                    style={[
                      styles.toggleCircle,
                      filters.delinquentOnly && styles.toggleCircleActive,
                    ]}
                  />
                </Pressable>
              </View>
              <View style={styles.filterGroup}>
                <Text style={styles.filterLabel}>Hidden clinic keywords</Text>
                {excludedClinicTerms.length ? (
                  <View style={styles.tagGrid}>
                    {excludedClinicTerms.map((term) => (
                      <View style={styles.tagPill} key={term}>
                        <Text style={styles.tagText}>{term}</Text>
                        <Pressable
                          onPress={() => handleRemoveExcludedTerm(term)}
                          style={styles.tagClose}
                        >
                          <Text style={styles.tagCloseText}>x</Text>
                        </Pressable>
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text style={styles.toggleHint}>
                    No keywords – all clinics visible.
                  </Text>
                )}
                <View style={styles.tagInputRow}>
                  <View style={styles.tagInputField}>
                    <Input
                      name="Add keyword"
                      placeholder="e.g. internal"
                      value={newExcludedTerm}
                      onChangeText={setNewExcludedTerm}
                      autoCapitalize="none"
                    />
                  </View>
                  <PrimaryButton
                    text="Add"
                    onPress={handleAddExcludedTerm}
                    disabled={!newExcludedTerm.trim()}
                  />
                </View>
              </View>
              <SecondaryButton
                text="Export delinquent CSV"
                onPress={handleExportDelinquentClinics}
                disabled={!delinquentClinics.length}
                style={styles.exportButton}
              />
            </View>

            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Pressable
                  style={[styles.tableHeaderCell, styles.clinicColumn]}
                  onPress={() => handleSort("clinic")}
                >
                  <Text style={styles.tableHeaderText}>
                    Clinic{" "}
                    {sortState?.column === "clinic" &&
                      (sortState.direction === "asc" ? "↑" : "↓")}
                  </Text>
                </Pressable>
                <Pressable
                  style={styles.tableHeaderCell}
                  onPress={() => handleSort("clinic_created")}
                >
                  <Text style={styles.tableHeaderText}>
                    Created{" "}
                    {sortState?.column === "clinic_created" &&
                      (sortState.direction === "asc" ? "↑" : "↓")}
                  </Text>
                </Pressable>
                <Pressable
                  style={styles.tableHeaderCell}
                  onPress={() => handleSort("plan")}
                >
                  <Text style={styles.tableHeaderText}>
                    Plan{" "}
                    {sortState?.column === "plan" &&
                      (sortState.direction === "asc" ? "↑" : "↓")}
                  </Text>
                </Pressable>
                <Pressable
                  style={styles.tableHeaderCell}
                  onPress={() => handleSort("delinquent")}
                >
                  <Text style={styles.tableHeaderText}>
                    Delinquent{" "}
                    {sortState?.column === "delinquent" &&
                      (sortState.direction === "asc" ? "↑" : "↓")}
                  </Text>
                </Pressable>
                <Pressable
                  style={styles.tableHeaderCell}
                  onPress={() => handleSort("remaining_sims")}
                >
                  <Text style={styles.tableHeaderText}>
                    Free sims left{" "}
                    {sortState?.column === "remaining_sims" &&
                      (sortState.direction === "asc" ? "↑" : "↓")}
                  </Text>
                </Pressable>
                <Pressable
                  style={[styles.tableHeaderCell, styles.invoiceColumn]}
                  onPress={() => handleSort("latest_invoice")}
                >
                  <Text style={styles.tableHeaderText}>
                    Latest invoice{" "}
                    {sortState?.column === "latest_invoice" &&
                      (sortState.direction === "asc" ? "↑" : "↓")}
                  </Text>
                </Pressable>
              </View>

              {filteredClinics.length === 0 && (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyTitle}>
                    No clinics match filters
                  </Text>
                  <Text style={styles.emptyText}>
                    Adjust filters or confirm that billing data has been synced.
                  </Text>
                </View>
              )}

              {filteredClinics.map((clinic) => (
                <Pressable
                  key={clinic.clinic_uuid}
                  style={[
                    styles.tableRow,
                    selectedClinicId === clinic.clinic_uuid &&
                      styles.tableRowSelected,
                  ]}
                  onPress={() => handleFetchClinicHistory(clinic)}
                >
                  <View style={[styles.tableCell, styles.clinicColumn]}>
                    <Text style={styles.clinicName}>{clinic.clinic_name}</Text>
                    <Text style={styles.clinicUuid}>{clinic.clinic_uuid}</Text>
                    <SecondaryButton
                      text={
                        copiedField === clinic.clinic_uuid
                          ? "Copied"
                          : "Copy UUID"
                      }
                      onPress={() =>
                        handleCopy(clinic.clinic_uuid, clinic.clinic_uuid)
                      }
                      style={styles.copyButton}
                    />
                  </View>
                  <View style={styles.tableCell}>
                    <Text style={styles.tableText}>
                      {formatTimestamp(getClinicCreatedAt(clinic)).local}
                    </Text>
                  </View>
                  <View style={styles.tableCell}>
                    <Text style={styles.planPill}>
                      {getClinicPlan(clinic) || "—"}
                    </Text>
                  </View>
                  <View style={styles.tableCell}>
                    <Text
                      style={[
                        styles.delinquentPill,
                        clinic.is_delinquent
                          ? styles.delinquentPillDanger
                          : styles.delinquentPillHealthy,
                      ]}
                    >
                      {clinic.is_delinquent ? "Yes" : "No"}
                    </Text>
                  </View>
                  <View style={styles.tableCell}>
                    <Text style={styles.tableText}>
                      {clinic.remaining_free_simulation_count ?? "—"}
                    </Text>
                  </View>
                  <View style={[styles.tableCell, styles.invoiceColumn]}>
                    {renderInvoiceLink(clinic.latest_invoice)}
                  </View>
                </Pressable>
              ))}
            </View>
          </>
        )}
      </View>

      <View style={styles.historySection}>
        <View style={styles.historyHeader}>
          <Text style={styles.cardTitle}>Clinic history</Text>
          {selectedClinicId && (
            <Pressable
              onPress={() => {
                setSelectedClinicId(null);
                setClinicHistory(null);
                setClinicSignupUsers([]);
                setClinicUsersError(null);
              }}
            >
              <Text style={styles.breadcrumb}>Summary / Clinic history</Text>
            </Pressable>
          )}
        </View>

        {!selectedClinicId && (
          <Text style={styles.historyHint}>
            Select a clinic row to load subscription and invoice history.
          </Text>
        )}

        {isHistoryLoading && (
          <View style={styles.loaderRow}>
            <ActivityIndicator color="#00B7F4" />
            <Text style={styles.loaderText}>Fetching clinic history…</Text>
          </View>
        )}

        {historyError && !isHistoryLoading && (
          <View style={styles.errorCard}>
            <Text style={styles.errorTitle}>Unable to load history</Text>
            <Text style={styles.errorMessage}>{historyError}</Text>
          </View>
        )}

        {clinicHistory && !isHistoryLoading && (
          <View style={styles.historyCard}>
            <View style={styles.historyHeaderRow}>
              <View>
                <Text style={styles.historyClinicName}>
                  {clinicHistory.clinic_name || "Clinic"}
                </Text>
                <Text style={styles.historyClinicUuid}>
                  {clinicHistory.clinic_uuid}
                </Text>
              </View>
              <Text
                style={[
                  styles.delinquentPill,
                  clinicHistory.is_delinquent
                    ? styles.delinquentPillDanger
                    : styles.delinquentPillHealthy,
                ]}
              >
                {clinicHistory.is_delinquent
                  ? "Delinquent"
                  : "In Good Standing"}
              </Text>
            </View>
            <View style={styles.historyActions}>
              <SecondaryButton
                text={
                  copiedField === clinicHistory.clinic_uuid
                    ? "Copied"
                    : "Copy clinic UUID"
                }
                onPress={() =>
                  handleCopy(
                    clinicHistory.clinic_uuid,
                    clinicHistory.clinic_uuid,
                  )
                }
              />
              <PrimaryButton
                text="Back to summary"
                onPress={() => {
                  setSelectedClinicId(null);
                  setClinicHistory(null);
                  setHistoryError(null);
                  setClinicSignupUsers([]);
                  setClinicUsersError(null);
                }}
              />
            </View>

            <View style={styles.historySubsection}>
              <Text style={styles.cardTitle}>Clinic users</Text>
              {isClinicUsersLoading && (
                <Text style={styles.emptyText}>Loading clinic users...</Text>
              )}
              {clinicUsersError && !isClinicUsersLoading && (
                <Text style={styles.errorMessage}>{clinicUsersError}</Text>
              )}
              {!isClinicUsersLoading &&
                !clinicUsersError &&
                !clinicSignupUsers.length && (
                  <Text style={styles.emptyText}>
                    No signup users found for this clinic.
                  </Text>
                )}
              {!isClinicUsersLoading &&
                !clinicUsersError &&
                clinicSignupUsers.map((clinicUser) => {
                  const signupTimestamp = formatTimestamp(
                    clinicUser.signup_created_at,
                  );
                  return (
                    <View
                      key={`${clinicUser.clinic_user_id}-${clinicUser.supabase_uuid}`}
                      style={styles.subscriptionRow}
                    >
                      <View style={styles.subscriptionCol}>
                        <Text style={styles.subscriptionLabel}>Email</Text>
                        <Text style={styles.subscriptionValue}>
                          {clinicUser.email || "No matching email"}
                        </Text>
                      </View>
                      <View style={styles.subscriptionCol}>
                        <Text style={styles.subscriptionLabel}>Role</Text>
                        <Text style={styles.subscriptionValue}>
                          {clinicUser.role}
                        </Text>
                      </View>
                      <View style={styles.subscriptionCol}>
                        <Text style={styles.subscriptionLabel}>Created</Text>
                        <Text
                          style={styles.subscriptionValue}
                          {...(Platform.OS === "web"
                            ? {
                                // @ts-ignore react-native-web prop
                                title: signupTimestamp.utc,
                              }
                            : {})}
                        >
                          {signupTimestamp.local}
                        </Text>
                      </View>
                      <View style={styles.subscriptionCol}>
                        <Text style={styles.subscriptionLabel}>
                          Supabase UUID
                        </Text>
                        <Text style={styles.subscriptionValue}>
                          {clinicUser.supabase_uuid}
                        </Text>
                      </View>
                    </View>
                  );
                })}
            </View>

            <View style={styles.historySubsection}>
              <Text style={styles.cardTitle}>Subscriptions</Text>
              {!clinicHistorySubscriptions.length && (
                <Text style={styles.emptyText}>
                  No subscriptions on record.
                </Text>
              )}
              {clinicHistorySubscriptions.map((subscription) => (
                <View key={subscription.id} style={styles.subscriptionRow}>
                  <View style={styles.subscriptionCol}>
                    <Text style={styles.subscriptionLabel}>ID</Text>
                    <Text style={styles.subscriptionValue}>
                      {subscription.id}
                    </Text>
                  </View>
                  <View style={styles.subscriptionCol}>
                    <Text style={styles.subscriptionLabel}>Status</Text>
                    <Text style={styles.subscriptionValue}>
                      {subscription.status}
                    </Text>
                  </View>
                  <View style={styles.subscriptionCol}>
                    <Text style={styles.subscriptionLabel}>Current period</Text>
                    {(() => {
                      const start = formatEpochLike(
                        subscription.current_period_start,
                      );
                      const end = formatEpochLike(
                        subscription.current_period_end,
                      );
                      return (
                        <Text
                          style={styles.subscriptionValue}
                          {...(Platform.OS === "web"
                            ? {
                                // @ts-ignore react-native-web prop
                                title: `${start.utc} → ${end.utc}`,
                              }
                            : {})}
                        >
                          {start.local} → {end.local}
                        </Text>
                      );
                    })()}
                  </View>
                  <View style={styles.subscriptionCol}>
                    <Text style={styles.subscriptionLabel}>Cancel at term</Text>
                    <Text style={styles.subscriptionValue}>
                      {subscription.cancel_at_period_end ? "Yes" : "No"}
                    </Text>
                  </View>
                </View>
              ))}
            </View>

            <View style={styles.historySubsection}>
              <Text style={styles.cardTitle}>Invoices</Text>
              {!clinicHistoryInvoices.length && (
                <Text style={styles.emptyText}>No invoices available.</Text>
              )}
              {clinicHistoryInvoices.map((invoice) => {
                const createdTimestamp = formatTimestamp(invoice.created_at);
                const nextAttempt = formatEpochSeconds(
                  invoice.next_payment_attempt,
                );
                return (
                  <View key={invoice.id} style={styles.invoiceRow}>
                    <View style={styles.invoiceMeta}>
                      <Text style={styles.invoiceId}>{invoice.id}</Text>
                      <Text style={styles.invoiceStatus}>
                        {invoice.status || "unknown"}
                      </Text>
                      <Text style={styles.invoiceAmount}>
                        {formatCurrency(
                          invoice.amount_paid,
                          invoice.currency || "usd",
                        )}
                      </Text>
                    </View>
                    <View style={styles.invoiceDates}>
                      <Text
                        style={styles.invoiceTimestamp}
                        {...(Platform.OS === "web"
                          ? {
                              // @ts-ignore react-native-web prop
                              title: createdTimestamp.utc,
                            }
                          : {})}
                      >
                        Created: {createdTimestamp.local}
                      </Text>
                      <Text
                        style={styles.invoiceTimestamp}
                        {...(Platform.OS === "web"
                          ? {
                              // @ts-ignore react-native-web prop
                              title: nextAttempt.utc,
                            }
                          : {})}
                      >
                        Next attempt: {nextAttempt.local}
                      </Text>
                    </View>
                    {invoice.hosted_invoice_url && (
                      <View style={styles.historyInvoiceActions}>
                        <Text
                          style={styles.invoiceLink}
                          onPress={() => {
                            if (typeof window !== "undefined") {
                              window.open(
                                invoice.hosted_invoice_url!,
                                "_blank",
                              );
                            }
                          }}
                        >
                          Open invoice
                        </Text>
                        <SecondaryButton
                          text={
                            copiedField === invoice.hosted_invoice_url
                              ? "Copied"
                              : "Copy link"
                          }
                          onPress={() =>
                            handleCopy(
                              invoice.hosted_invoice_url!,
                              invoice.hosted_invoice_url!,
                            )
                          }
                          style={styles.copyButton}
                        />
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#F5F7FA",
  },
  invoiceLinkDisabled: {
    color: "#9CA3AF",
    textDecorationLine: "none",
    opacity: 0.6,
  },
  pageContent: {
    padding: 32,
    gap: 24,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  headerTextGroup: {
    flex: 1,
    minWidth: 240,
    gap: 8,
  },
  subtitle: {
    fontFamily: "Urbanist400",
    fontSize: 16,
    color: "#4E5565",
  },
  badgeWrapper: {
    alignItems: "flex-end",
    gap: 8,
  },
  authBadgeLabel: {
    fontFamily: "Urbanist500",
    fontSize: 14,
    color: "#6A6F80",
  },
  authBadge: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 999,
    backgroundColor: "#C4EEFD",
    fontFamily: "Urbanist600",
    color: "#075985",
    fontSize: 14,
  },
  authBadgeIdle: {
    backgroundColor: "#E3E5EB",
    color: "#4E5565",
  },
  connectionCard: {
    backgroundColor: "#fff",
    borderRadius: 20,
    padding: 24,
    shadowColor: "#000",
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 10 },
    shadowRadius: 15,
    gap: 16,
  },
  cardTitle: {
    fontFamily: "Urbanist600",
    fontSize: 18,
    color: "#111827",
    marginBottom: 4,
  },
  cardSubtitle: {
    fontFamily: "Urbanist400",
    fontSize: 14,
    color: "#4E5565",
  },
  authToggleRow: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  authToggleButton: {
    minWidth: 160,
  },
  authContent: {
    gap: 16,
  },
  connectButton: {
    alignSelf: "flex-start",
  },
  resetButton: {
    alignSelf: "flex-start",
  },
  jwtInfo: {
    gap: 4,
  },
  jwtConnected: {
    fontFamily: "Urbanist500",
    fontSize: 16,
    color: "#1F2937",
  },
  jwtHint: {
    fontFamily: "Urbanist400",
    fontSize: 14,
    color: "#4E5565",
  },
  warningCard: {
    borderRadius: 16,
    backgroundColor: "#FEF3C7",
    borderColor: "#FBBF24",
    borderWidth: 1,
    padding: 16,
    gap: 4,
  },
  warningTitle: {
    fontFamily: "Urbanist600",
    color: "#92400E",
    fontSize: 16,
  },
  warningMessage: {
    fontFamily: "Urbanist400",
    color: "#92400E",
    fontSize: 14,
  },
  summarySection: {
    backgroundColor: "#fff",
    borderRadius: 20,
    padding: 24,
    gap: 16,
    shadowColor: "#000",
    shadowOpacity: 0.03,
    shadowOffset: { width: 0, height: 12 },
    shadowRadius: 20,
  },
  loaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  loaderText: {
    fontFamily: "Urbanist500",
    color: "#4E5565",
  },
  errorCard: {
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: "#FEE2E2",
    backgroundColor: "#FEF2F2",
    gap: 8,
  },
  errorTitle: {
    fontFamily: "Urbanist600",
    color: "#991B1B",
    fontSize: 16,
  },
  errorMessage: {
    fontFamily: "Urbanist400",
    color: "#991B1B",
    fontSize: 14,
  },
  retryButton: {
    alignSelf: "flex-start",
  },
  kpiRow: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  kpiCard: {
    flex: 1,
    minWidth: 160,
    backgroundColor: "#F8FAFC",
    borderRadius: 16,
    padding: 16,
    gap: 4,
  },
  kpiLabel: {
    fontFamily: "Urbanist500",
    color: "#6B7280",
    fontSize: 14,
  },
  kpiValue: {
    fontFamily: "Urbanist700",
    color: "#111827",
    fontSize: 32,
  },
  kpiValueDanger: {
    fontFamily: "Urbanist700",
    color: "#B91C1C",
    fontSize: 32,
  },
  filterRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    alignItems: "flex-end",
  },
  filterGroup: {
    flex: 1,
    minWidth: 220,
    gap: 8,
  },
  filterLabel: {
    fontFamily: "Urbanist500",
    color: "#4E5565",
  },
  planMixSection: {
    gap: 8,
  },
  planMixList: {
    gap: 8,
  },
  planMixRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  planMixLabel: {
    minWidth: 140,
    fontFamily: "Urbanist500",
    color: "#111827",
  },
  planMixBarTrack: {
    flex: 1,
    height: 10,
    borderRadius: 999,
    backgroundColor: "#E5E7EB",
    overflow: "hidden",
  },
  planMixBarFill: {
    height: "100%",
    borderRadius: 999,
    backgroundColor: "#0EA5E9",
  },
  planMixCount: {
    minWidth: 28,
    textAlign: "right",
    fontFamily: "Urbanist600",
    color: "#4E5565",
  },
  planFilterRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  planFilterButton: {
    minWidth: 110,
  },
  togglePill: {
    width: 50,
    height: 28,
    borderRadius: 999,
    backgroundColor: "#E5E7EB",
    justifyContent: "center",
    paddingHorizontal: 4,
  },
  togglePillActive: {
    backgroundColor: "#0EA5E9",
  },
  toggleCircle: {
    width: 20,
    height: 20,
    borderRadius: 999,
    backgroundColor: "#fff",
    alignSelf: "flex-start",
  },
  toggleCircleActive: {
    alignSelf: "flex-end",
  },
  toggleHint: {
    fontFamily: "Urbanist400",
    fontSize: 12,
    color: "#6B7280",
  },
  tagGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  tagPill: {
    position: "relative",
    paddingVertical: 6,
    paddingHorizontal: 16,
    paddingRight: 26,
    borderRadius: 999,
    backgroundColor: "#E0F2FE",
  },
  tagText: {
    fontFamily: "Urbanist600",
    color: "#0F172A",
  },
  tagClose: {
    position: "absolute",
    top: -6,
    right: -6,
    width: 20,
    height: 20,
    borderRadius: 999,
    backgroundColor: "#0EA5E9",
    alignItems: "center",
    justifyContent: "center",
  },
  tagCloseText: {
    fontFamily: "Urbanist700",
    color: "#fff",
    fontSize: 12,
    lineHeight: 12,
  },
  tagInputRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-end",
  },
  tagInputField: {
    flex: 1,
  },
  exportButton: {
    alignSelf: "flex-start",
  },
  table: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 16,
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
  },
  tableHeaderCell: {
    flex: 1,
    padding: 12,
    borderRightWidth: 1,
    borderColor: "#E5E7EB",
  },
  clinicColumn: {
    flex: 1.6,
  },
  invoiceColumn: {
    flex: 1.6,
  },
  tableHeaderText: {
    fontFamily: "Urbanist600",
    fontSize: 14,
    color: "#4B5563",
  },
  tableRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#fff",
  },
  tableRowSelected: {
    backgroundColor: "#ECFEFF",
  },
  tableCell: {
    flex: 1,
    padding: 12,
    borderRightWidth: 1,
    borderColor: "#E5E7EB",
    gap: 4,
  },
  tableText: {
    fontFamily: "Urbanist500",
    color: "#111827",
  },
  clinicName: {
    fontFamily: "Urbanist600",
    color: "#111827",
  },
  clinicUuid: {
    fontFamily: "Urbanist400",
    color: "#4E5565",
    fontSize: 13,
  },
  planPill: {
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: "#EEF2FF",
    color: "#4338CA",
    fontFamily: "Urbanist600",
  },
  delinquentPill: {
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    fontFamily: "Urbanist600",
    color: "#fff",
  },
  delinquentPillDanger: {
    backgroundColor: "#DC2626",
  },
  delinquentPillHealthy: {
    backgroundColor: "#059669",
  },
  invoiceCell: {
    gap: 4,
  },
  invoiceId: {
    fontFamily: "Urbanist600",
    color: "#111827",
  },
  invoiceStatus: {
    fontFamily: "Urbanist500",
    color: "#4E5565",
    textTransform: "capitalize",
  },
  invoiceLink: {
    fontFamily: "Urbanist600",
    color: "#0EA5E9",
    textDecorationLine: "underline",
  },
  invoiceTimestamp: {
    fontFamily: "Urbanist400",
    color: "#6B7280",
    fontSize: 12,
  },
  invoiceAmount: {
    fontFamily: "Urbanist600",
    color: "#111827",
    fontSize: 16,
  },
  copyButton: {
    alignSelf: "flex-start",
  },
  emptyState: {
    padding: 24,
    alignItems: "center",
    gap: 4,
  },
  emptyTitle: {
    fontFamily: "Urbanist600",
    color: "#111827",
  },
  emptyText: {
    fontFamily: "Urbanist400",
    color: "#4E5565",
  },
  historySection: {
    backgroundColor: "#fff",
    borderRadius: 20,
    padding: 24,
    gap: 16,
  },
  historyHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  breadcrumb: {
    fontFamily: "Urbanist500",
    color: "#2563EB",
    textDecorationLine: "underline",
  },
  historyHint: {
    fontFamily: "Urbanist400",
    color: "#4E5565",
  },
  historyCard: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 16,
    padding: 16,
    gap: 16,
  },
  historyHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  historyClinicName: {
    fontFamily: "Urbanist700",
    fontSize: 20,
  },
  historyClinicUuid: {
    fontFamily: "Urbanist400",
    color: "#6B7280",
  },
  historyActions: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  historySubsection: {
    gap: 12,
  },
  subscriptionRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 12,
  },
  subscriptionCol: {
    minWidth: 160,
  },
  subscriptionLabel: {
    fontFamily: "Urbanist500",
    color: "#6B7280",
    fontSize: 13,
  },
  subscriptionValue: {
    fontFamily: "Urbanist600",
    color: "#111827",
  },
  invoiceRow: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 12,
    padding: 12,
    gap: 8,
  },
  invoiceMeta: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
  },
  invoiceDates: {
    gap: 4,
  },
  historyInvoiceActions: {
    flexDirection: "row",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
});

export default BillingAdminPage;
